import {
  AbstractControl,
  FormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Address } from '@core/models';
import {
  OiqFormAddressEmpty,
  OiqFormAddressValidator,
  OiqFormSsnLengthValidator,
} from '@oiq-ui-sdk/form';
import moment from 'moment';
import { startWith } from 'rxjs/operators';

export class OiqCreditCheckForm extends UntypedFormGroup {
  exclude$ = this.get('exclude').valueChanges.pipe(startWith(true));

  private _hasFeature: boolean = true;

  get hasFeature(): boolean {
    return this._hasFeature;
  }

  set hasFeature(v: boolean) {
    this._hasFeature = v;
    this._hasFeature ? this.get('exclude').enable() : this.get('exclude').disable();
    if (!this._hasFeature) {
      this.get('exclude').setValue(true);
    }
  }

  get personalFields(): string[] {
    return ['ssn', 'dob', 'address'];
  }

  get addressFields(): string[] {
    return ['address1', 'address2', 'state', 'zipCode', 'city'];
  }

  get contactId(): any {
    return this.get('contactId').value;
  }

  constructor() {
    super(
      {
        contactId: new UntypedFormControl(null, Validators.required),
        primary: new UntypedFormControl(false),
        // personal
        firstName: new UntypedFormControl('', Validators.required),
        lastName: new UntypedFormControl('', Validators.required),
        ssn: new UntypedFormControl('', [OiqFormSsnLengthValidator]),
        dob: new UntypedFormControl('', []),
        address: new UntypedFormControl(null, [OiqFormAddressValidator]),
        exclude: new UntypedFormControl(true),
      },
      {
        updateOn: 'change',
      }
    );
    this.get('primary').disable();
    this.get('ssn').addValidators([this._validateRequiredField.bind(this)]);
    this.get('address').addValidators([this._validateRequiredAdress.bind(this)]);
    this.get('dob').addValidators([this._validateRequiredField.bind(this)]);

    this.exclude$.subscribe((_excluded: boolean) => {
      this.get('address').updateValueAndValidity();
      this.personalFields.forEach(fieldToRevalidate => {
        const _ctrl = this.get(fieldToRevalidate);
        _ctrl.updateValueAndValidity();
      });
    });
  }

  private _expanded: boolean = false;
  public get expanded(): boolean {
    return this._expanded;
  }
  public set expanded(v: boolean) {
    this._expanded = v;
    this.markAllAsTouched();
  }

  get isExcluded(): boolean {
    return !!this.get('exclude').value;
  }

  get isPrimary(): boolean {
    return !!this.get('primary').value;
  }

  toggleExclude() {
    this.get('exclude').setValue(!this.isExcluded);
  }

  private _validateRequiredField(control: AbstractControl): ValidationErrors | null {
    if (this.isExcluded) {
      return null;
    }

    return Validators.required(control);
  }

  private _validateRequiredAdress(control: AbstractControl): ValidationErrors | null {
    if (this.isExcluded) {
      return null;
    }

    return OiqFormAddressEmpty(control.value || {})
      ? {
          required: true,
        }
      : null;
  }

  fromOwnerResponse(owner: any) {
    if (!owner) {
      this.patchValue({
        primary: false,
        contactId: null,
        firstName: '',
        lastName: '',
        ssn: '',
        address: null,
        dob: null,
        exclude: true,
      });
      return;
    }

    this.patchValue({
      primary: owner.primary,
      contactId: owner.contact.id,
      firstName: owner.contact.firstName,
      lastName: owner.contact.lastName,
      ssn: owner.contact.ssn,
      address: {
        address1: owner.contact.address ? owner.contact.address.address1 : '',
        address2: owner.contact.address ? owner.contact.address.address2 : '',
        city: owner.contact.address ? owner.contact.address.city : '',
        state: owner.contact.address ? owner.contact.address.state : '',
        zipCode: owner.contact.address ? owner.contact.address.zipCode : '',
      },
      dob: owner.contact.dob ? owner.contact.dob : null,
      exclude: true,
    });
  }

  get toPayload(): any {
    const _raw = this.getRawValue();
    const _address = _raw['address'] || {};
    const _payload = {
      ..._raw,
      address1: _address.address1 || '',
      address2: _address.address2 || '',
      city: _address.city || '',
      state: _address.state || '',
      zipCode: _address.zipCode || '',
      dob: moment(_raw.dob).isValid() ? moment(_raw.dob).format('YYYY-MM-DD') : '',
    };

    delete _payload['address'];

    return _payload;
  }

  getAddressPayload(client: any) {
    const _payload = this.toPayload;

    const owner = client.owners.find((r: any) => {
      return _payload.contactId === r.contactId;
    });
    _payload.id = owner && owner.contact && owner.contact.address ? owner.contact.address.id : null;

    const _address = new Address(_payload);
    const _addressId = _address.value.id || null;

    const _contact = owner && owner.contact ? owner.contact : null;
    const _contactAddresses = _contact && _contact.addresses ? _contact.addresses : [];
    const _addresses: Address[] = _contactAddresses.map(
      (_contactAddress: any) => new Address(_contactAddress)
    );
    const _foundAddress = _addresses.find((a: any) => a.value.id === _addressId);

    if (_address.value.address1.trim() !== '') {
      // address is to be changed
      if (_foundAddress) {
        // it exists in the list by id, we are updating it
        _foundAddress.patchValue(_address.value);
      } else {
        // does not exists in the list, we are adding it
        _addresses.push(_address);
      }
    } else {
      // invalid state, so reset to default
      _address.patchValue(owner.contact.address);
    }

    return {
      id: _payload.contactId,
      address: _address.value,
      addresses: _addresses.map((a: Address) => a.value),
      ssn: _payload.ssn,
      dob: moment(_payload.dob).isValid() ? moment(_payload.dob).format('YYYY-MM-DD') : '',
    };
  }
}

export class OiqCreditCheckArray extends FormArray<OiqCreditCheckForm> {
  private _excludeFromCreditCheck: boolean = true;
  get excludeFromCreditCheck(): any {
    return this._excludeFromCreditCheck;
  }
  set excludeFromCreditCheck(value: boolean) {
    this._excludeFromCreditCheck = value || false;
  }

  private _owners: any[] = [];
  private _hasFeature: boolean = true;
  get hasFeature(): boolean {
    return this._hasFeature;
  }
  set hasFeature(v: boolean) {
    this._hasFeature = v;
    this.controls.forEach(creditCheckRequest => (creditCheckRequest.hasFeature = v));
  }

  constructor() {
    super([]);
    this.disable();
  }
  setOwners(owners: any[], creditCheckData?: any): void {
    this._owners = owners || [];
    this.clear();

    // if (!this.hasFeature) {
    //   return;
    // }

    if (this._owners.length === 0) {
      this.disable();
      return;
    }

    this.enable();

    this._owners.forEach(owner => {
      // Check if this contact should be excluded based on store credit check data
      let shouldExclude = this.excludeFromCreditCheck;
      if (
        creditCheckData &&
        creditCheckData.contact &&
        creditCheckData.contact.id === owner.contact.id
      ) {
        // Note: include means NOT exclude, so we need to invert it
        shouldExclude = !creditCheckData.include;
      }

      const creditCheckRequest = new OiqCreditCheckForm();
      creditCheckRequest.hasFeature = this.hasFeature;
      creditCheckRequest.fromOwnerResponse(owner);
      creditCheckRequest.get('exclude').setValue(!this.hasFeature ? true : shouldExclude);

      this.push(creditCheckRequest);
    });
  }

  updateFromOcrData(ocrData: any) {
    this.controls.forEach(creditCheckRequest => {
      if (creditCheckRequest.get('primary').value) {
        const _originalValue = creditCheckRequest.value;
        creditCheckRequest.patchValue({
          firstName: ocrData.contactFirstName?.value || _originalValue.firstName,
          lastName: ocrData.contactLastName?.value || _originalValue.lastName,
          ssn: ocrData.contactSsn?.value || _originalValue.ssn,
          dob: ocrData.contactDob?.value || _originalValue.dob,
          address1: ocrData.contactAddress1?.value || _originalValue.address1,
          city: ocrData.contactAddressCity?.value || _originalValue.city,
          state: ocrData.contactAddressState?.value || _originalValue.state,
          zipCode: ocrData.contactAddressZip?.value || _originalValue.zipCode,
        });
      }
    });
  }

  setPrimaryOwner(ownerToUpdate: OiqCreditCheckForm) {
    const contactId = ownerToUpdate.contactId;

    const previousState = this.controls.map(owner => ({
      contactId: owner.contactId,
      isPrimary: !!owner.get('primary').value,
    }));

    this.controls.forEach(owner => {
      const _isSameContactId = owner.contactId === contactId;
      owner.get('primary').patchValue(_isSameContactId);
    });

    return previousState;
  }

  restorePrimaryOwner(previousState: any[]) {
    previousState.forEach(owner => {
      const _owner = this.controls.find(_owner => _owner.contactId === owner.contactId);
      _owner.get('primary').patchValue(owner.isPrimary);
    });
  }

  get toPayload() {
    if (!this.hasFeature) {
      return [];
    }
    return this.controls.filter(_control => !_control.isExcluded).map(control => control.toPayload);
  }
}
